import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  GroupDescriptor,
} from '@progress/kendo-data-query';
import { DATASET_INITIAL_ID, SLICE_FILTER_GLOBALS } from 'common/constants';
import { useWidgetOptionsContext } from 'common/contexts/useWidgetOptionsContext';
import {
  applyFiltersAndGroups,
  normalizeFiltersServerSide,
  normalizeSort,
} from 'common/hooks/filters';
import usePageFilters from 'common/hooks/usePageFilters';
import { ChartFilterModel } from 'common/models/filter.model';
import { WidgetDataRequestModel, WidgetModel } from 'common/models/widget.model';
import { RootState } from 'common/store';
import { selectChartFilters } from 'common/store/slices/chart-filters.slice';
import { selectDatasetQuestion } from 'common/store/slices/dataset.slice';
import { selectGridConfigById } from 'common/store/slices/grid-config.slice';
import { selectWidgetAggregatesById } from 'common/store/slices/page-composer.slice';
import { selectPagination } from 'common/store/slices/pagination.slice';
import { selectRequestFilterByWidgetId } from 'common/store/slices/request-filter.slice';
import { selectWidgetFilters } from 'common/store/slices/widget-filter.slice';
import { selectWidgetSort } from 'common/store/slices/widget-sort.slice';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

interface WidgetRequestBuilderArgs {
  widget: WidgetModel;
}

function useWidgetRequestBuilder({ widget }: WidgetRequestBuilderArgs) {
  const { groupBy, allIncluded } = useWidgetOptionsContext();

  const { projectId } = useParams();
  const widgetsAggregates = useSelector((state: RootState) =>
    selectWidgetAggregatesById(state, widget.id ?? '')
  );

  const datasetQuestion = useSelector((state: RootState) =>
    selectDatasetQuestion(state, widget.datasetId, projectId ?? '')
  );
  const chartFilters = useSelector((state: RootState) =>
    selectChartFilters(state, widget.datasetId ?? DATASET_INITIAL_ID)
  );
  const widgetRequestFilter = useSelector((state: RootState) =>
    selectRequestFilterByWidgetId(
      state,
      widget.originalWidgetId ? `${widget.originalWidgetId}-${widget.id}` : widget.id
    )
  );

  const widgetFilter = useSelector((state: RootState) => selectWidgetFilters(state, widget.id));

  const widgetSort = useSelector((state: RootState) => selectWidgetSort(state, widget.id));
  const pagination = useSelector((state: RootState) => selectPagination(state, widget.id));
  // Grids grouping is separate since we use grouping by the dropdowns on the charts
  const gridConfig = useSelector((state: RootState) => selectGridConfigById(state, widget.id));

  const currentWidgetGrouping: GroupDescriptor[] = gridConfig.group ?? [];
  const currentWidgetGroupBy =
    groupBy ??
    widget.selectedGroupBy ??
    widget?.defaultGroupBy ??
    widget?.groupOptions?.[0]?.memberName ??
    null;

  const currentWidgetAggregates =
    widget.selectedAggregates && widget?.selectedAggregates?.length > 0
      ? widget.selectedAggregates
      : widgetsAggregates ?? null;

  const { pageFilterKeys } = usePageFilters({ datasetId: widget.datasetId });

  if (!widget.request) {
    return null;
  }

  const widgetRequest: any = { ...widget.request };

  if (widget.hasQuestion && datasetQuestion) {
    const idGroup: number[] | undefined = datasetQuestion?.idGroup;

    const multiQuestionFilter = {
      logicalOperator: '1',
      filterDescriptors: [
        ...(idGroup?.map((id: number) => ({
          operator: '2',
          value: id,
          member: 'QuestionId',
        })) ?? []),
      ],
    };

    const singleQuestionFilter = {
      member: 'QuestionId',
      Operator: '2',
      Value: datasetQuestion.id,
    };

    const questionFilterOperator = idGroup ? multiQuestionFilter : singleQuestionFilter;
    widgetRequest.data = applyFiltersAndGroups(
      widgetRequest,
      questionFilterOperator,
      currentWidgetGroupBy
    );
  } else {
    widgetRequest.data = applyFiltersAndGroups(
      widgetRequest,
      null,
      currentWidgetGroupBy,
      currentWidgetAggregates
    );
  }

  if (currentWidgetAggregates) {
    widgetRequest.data = applyFiltersAndGroups(
      widgetRequest,
      null,
      currentWidgetGroupBy,
      currentWidgetAggregates
    );
  }

  if (chartFilters && chartFilters.length > 0) {
    const chartFiltersNotFromThisWidget = chartFilters.filter(
      (filter: ChartFilterModel) =>
        SLICE_FILTER_GLOBALS.includes(filter.propertyName) || filter.id != widget.id
    );
    chartFiltersNotFromThisWidget.forEach((filter: ChartFilterModel) => {
      const filterOperator = {
        member: filter.propertyName,
        Operator: '2',
        Value: filter.value,
      };
      widgetRequest.data = applyFiltersAndGroups(widgetRequest, filterOperator);
    });
  }

  if (widgetRequestFilter) {
    widgetRequest.data = applyFiltersAndGroups(widgetRequest, widgetRequestFilter[0]);
  }
  if (widgetFilter.length > 0) {
    if ((widgetFilter[0] as CompositeFilterDescriptor).logic.includes('bryntum')) {
      widgetFilter.forEach((filter) => {
        const { logic, ...rest } = filter as CompositeFilterDescriptor;

        widgetRequest.data = applyFiltersAndGroups(widgetRequest, {
          ...rest,
          logicalOperator: (filter as CompositeFilterDescriptor).logic.split('-')[1],
        });
      });
    } else {
      widgetFilter.forEach((filterPipeline: CompositeFilterDescriptor | FilterDescriptor) => {
        if ('logic' in filterPipeline && 'filters' in filterPipeline) {
          const result = normalizeFiltersServerSide(filterPipeline);
          widgetRequest.data = applyFiltersAndGroups(widgetRequest, result);
        }
      });
    }
  }
  if (widgetSort.length > 0) {
    widgetRequest.data = {
      ...widgetRequest.data,
      sorts: normalizeSort(widgetSort),
    };
  }
  if (currentWidgetGrouping.length > 0 && widget.groupable) {
    const normalizedGrouping = currentWidgetGrouping.map((group) => ({
      member: group.field,
      sortDirection: group.dir == 'asc' ? 0 : 1,
    }));
    widgetRequest.data = {
      ...widgetRequest.data,
      groups: [...widgetRequest.data.groups, ...normalizedGrouping],
    };
  }
  if (pagination) {
    widgetRequest.data.skip = pagination.skip;
    widgetRequest.data.pageSize = pagination.take;
  }

  // Unsafe return dissabled because we're actively butchering the request and it differs to what we're sending
  const validData = Array.isArray(widgetRequest.data)
    ? // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      widgetRequest.data.map((dataItem: any) => ({
        ...dataItem,
        filters:
          dataItem.filters?.filter((filter: any) => {
            // If the filter is a logical operator, we want to keep it
            if (filter.logicalOperator) {
              return true;
            }

            // Only allow filters that are in the pageFilterKeys array
            return !!pageFilterKeys.includes(filter.member);
          }) ?? [],
      }))
    : {
        ...widgetRequest.data,
        filters:
          widgetRequest.data?.filters?.filter((filter: any) => {
            return !!pageFilterKeys.includes(filter.member) || !!filter.logicalOperator;
          }) ?? [],
      };

  widgetRequest.data = validData;

  // if includeAll is set, add it to the query params
  if (allIncluded) {
    const [basePath, existingParams] = widgetRequest.path.split('?');
    const queryParams = new URLSearchParams(existingParams);
    queryParams.set('allIncluded', allIncluded.toString());
    widgetRequest.path = `${basePath}?${queryParams.toString()}`;
  }

  return widgetRequest as WidgetDataRequestModel;
}

export default useWidgetRequestBuilder;
